import { createTrustedJsonLdScript } from '@/utils/trustedScriptSrc'

type HookableHead = {
  hooks?: {
    hook?: unknown
  }
}

type HeadTagLike = {
  tag?: string
  props?: Record<string, unknown>
  textContent?: unknown
  _trustedJsonLdText?: string
}

type TagsAfterResolveContext = {
  tags: HeadTagLike[]
}

type DomRenderContext = {
  $el?: Element | null
  tag?: HeadTagLike
}

type DomRenderedContext = {
  renders: DomRenderContext[]
}

type HeadHook = (name: string, callback: (context: unknown) => void) => unknown

function isJsonLdScriptTag(tag: HeadTagLike): boolean {
  return tag.tag === 'script' && tag.props?.type === 'application/ld+json'
}

function getHeadHook(head: HookableHead): HeadHook | null {
  return typeof head.hooks?.hook === 'function'
    ? head.hooks.hook.bind(head.hooks) as HeadHook
    : null
}

/**
 * Keeps Unhead-managed JSON-LD client-rendered while satisfying Trusted Types.
 *
 * Unhead normally writes JSON-LD via `script.textContent = string`, which
 * reports a TrustedScript violation when `require-trusted-types-for 'script'`
 * is enabled. This plugin removes the raw string before Unhead touches the DOM,
 * then writes it back as a validated TrustedScript after the script element has
 * been rendered.
 */
export function installTrustedJsonLdHeadPlugin(head: HookableHead): void {
  const hook = getHeadHook(head)

  if (!hook) {
    return
  }

  hook('tags:afterResolve', (context) => {
    const { tags } = context as TagsAfterResolveContext

    for (const tag of tags) {
      if (!isJsonLdScriptTag(tag) || typeof tag.textContent !== 'string') {
        continue
      }

      tag._trustedJsonLdText = tag.textContent
      delete tag.textContent
    }
  })

  hook('dom:rendered', (context) => {
    const { renders } = context as DomRenderedContext

    for (const render of renders) {
      const jsonLdText = render.tag?._trustedJsonLdText

      if (!jsonLdText || !(render.$el instanceof HTMLScriptElement)) {
        continue
      }

      render.$el.textContent = createTrustedJsonLdScript(jsonLdText)
    }
  })
}
